import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { TApiaCellDefinition, TApiaComplexCell } from '../../types';
import checkModalValue from './newApi/util/checkModalValue';
import {
  getFiltersConditions,
  setFiltersConditions,
} from './newApi/util/filtersConditions';
import {
  TFinderPayload,
  IFinderSlice,
  TFinderState,
  TApiaFinderFilter,
  TSetInitialState,
} from './types';
import getRowData from './newApi/util/getRowData';
import sorter from './newApi/actions/queryActions/sort';
import searcher from './newApi/actions/queryActions/search';
import { TResponsiveTableColumn } from '../../components/common/generic/responsiveTable/types';
import addQueryActions from './newApi/actions/queryActions';
import updateFilter from './newApi/actions/queryActions/updateFilters';
import refresh from './newApi/actions/queryActions/refresh';
import deleteFilters from './newApi/actions/queryActions/deleteFilters';
import page from './newApi/actions/queryActions/page';
import updateData from './newApi/actions/queryActions/updateData';
import loadStructures from './newApi/actions/queryActions/loadStructure';
import loadSavedFilters from './newApi/actions/queryActions/loadSavedFilters';
import setFilters from './newApi/actions/queryActions/setFilters';

const getInitialState: (props: TSetInitialState) => TFinderState = (
  initialState: TSetInitialState,
) => {
  return {
    areAllFiltersApplied: true,
    currentChart: undefined,
    currentPivot: undefined,
    freeMetaFilters: [],
    hasAdditionalInfoInTable: false,
    hasSearched: false,
    inputValue: '',
    lastSearchAppliedFilters: 0,
    loading: 0,
    metaFilters: {
      allowFreeMeta: false,
      processInfo: '',
      docType: undefined,
      filters: [],
    },
    shouldShowModal: false,
    rows: [],
    ...initialState,
    ajaxUrl: initialState.ajaxUrl ?? window.URL_REQUEST_AJAX,
  };
};

export function isComplexCell(
  cell: TApiaCellDefinition,
): cell is TApiaComplexCell {
  return typeof cell !== 'string';
}

export const initLoad = (state: Draft<IFinderSlice>, fieldId: string) => {
  state.queries[fieldId].loading += 1;
};

export const finishLoad = (
  state: Draft<IFinderSlice>,
  fieldId: string,
  countFilters = true,
  filters?: TApiaFinderFilter[],
) => {
  state.queries[fieldId].loading -= 1;
  if (countFilters) {
    if (filters) {
      const appliedFilters = new Set();

      filters?.forEach((incomingFilter) => {
        if (
          ['date', 'D'].includes(incomingFilter.apiaFilter.type as string) ||
          incomingFilter.apiaFilter.isRange
        ) {
          if (
            incomingFilter.apiaFilter.currentValue !== '' &&
            incomingFilter.apiaFilter.currentValue !== undefined
          ) {
            appliedFilters.add(incomingFilter.apiaFilter.id);
          } else if (incomingFilter.apiaFilter.currentValue === '') {
            appliedFilters.delete(incomingFilter.apiaFilter.id);
          }
          if (
            incomingFilter.apiaFilter.filterToValue !== '' &&
            incomingFilter.apiaFilter.filterToValue !== undefined
          ) {
            appliedFilters.add(incomingFilter.apiaFilter.filterToId);
          } else if (incomingFilter.apiaFilter.filterToValue === '') {
            appliedFilters.delete(incomingFilter.apiaFilter.filterToId);
          }
        } else if (
          incomingFilter.apiaFilter.currentValue !== '' &&
          incomingFilter.apiaFilter.currentValue !== undefined
        ) {
          appliedFilters.add(incomingFilter.apiaFilter.id);
        } else if (incomingFilter.apiaFilter.currentValue === '') {
          appliedFilters.delete(incomingFilter.apiaFilter.id);
        }
      });
      state.queries[fieldId].lastSearchAppliedFilters = appliedFilters.size;
    } else {
      const appliedFilters = new Set();

      state.queries[fieldId].filters?.forEach((incomingFilter) => {
        if (
          ['date', 'D'].includes(incomingFilter.apiaFilter.type as string) ||
          incomingFilter.apiaFilter.isRange
        ) {
          if (
            incomingFilter.apiaFilter.currentValue !== '' &&
            incomingFilter.apiaFilter.currentValue !== undefined
          ) {
            appliedFilters.add(incomingFilter.apiaFilter.id);
          } else if (incomingFilter.apiaFilter.currentValue === '') {
            appliedFilters.delete(incomingFilter.apiaFilter.id);
          }
          if (
            incomingFilter.apiaFilter.filterToValue !== '' &&
            incomingFilter.apiaFilter.filterToValue !== undefined
          ) {
            appliedFilters.add(incomingFilter.apiaFilter.filterToId);
          } else if (incomingFilter.apiaFilter.filterToValue === '') {
            appliedFilters.delete(incomingFilter.apiaFilter.filterToId);
          }
        } else if (
          incomingFilter.apiaFilter.currentValue !== '' &&
          incomingFilter.apiaFilter.currentValue !== undefined
        ) {
          appliedFilters.add(incomingFilter.apiaFilter.id);
        } else if (incomingFilter.apiaFilter.currentValue === '') {
          appliedFilters.delete(incomingFilter.apiaFilter.id);
        }
      });

      state.queries[fieldId].lastSearchAppliedFilters = appliedFilters.size;
    }
  }
  if (state.queries[fieldId].loading < 0) state.queries[fieldId].loading = 0;
};

type TAdminPayload<T> = PayloadAction<{ id: string } & T>;

const querySlice = createSlice({
  name: 'query',
  initialState: {
    queries: {},
  } as IFinderSlice,
  reducers: {
    addFilters(
      state,
      {
        payload,
      }: PayloadAction<{
        fieldId: string;
        newFilters: TApiaFinderFilter[];
        changeExisting?: boolean;
      }>,
    ) {
      if (payload.changeExisting) {
        state.queries[payload.fieldId].filters = [
          ...(state.queries[payload.fieldId].filters ?? []).filter(
            (current) =>
              !payload.newFilters.find(
                (searchFilter) =>
                  searchFilter.apiaFilter.id === current.apiaFilter.id,
              ),
          ),
          ...(state.queries[payload.fieldId].filters ?? [])
            .filter((current) =>
              payload.newFilters.find(
                (searchFilter) =>
                  searchFilter.apiaFilter.id === current.apiaFilter.id,
              ),
            )
            .map((filter) => {
              const changedFilterIndex = payload.newFilters.findIndex(
                (searchFilter) =>
                  searchFilter.apiaFilter.id === filter.apiaFilter.id,
              );
              return {
                ...filter,
                apiaFilter: {
                  ...filter?.apiaFilter,
                  ...payload.newFilters[changedFilterIndex].apiaFilter,
                },
              };
            }),
        ];
      } else {
        state.queries[payload.fieldId].filters = [
          ...(state.queries[payload.fieldId].filters ?? []).filter(
            (current) =>
              !payload.newFilters.find(
                (searchFilter) =>
                  searchFilter.apiaFilter.id === current.apiaFilter.id,
              ),
          ),
          ...payload.newFilters,
        ];
      }
    },
    initState(
      state,
      {
        payload: { fieldId, initialState },
      }: PayloadAction<{
        fieldId: string;
        initialState: TSetInitialState;
      }>,
    ) {
      state.queries[fieldId] = getInitialState(initialState);
    },
    setState: (
      state,
      {
        payload: { fieldId, ...payload },
      }: TFinderPayload<Partial<TFinderState>>,
    ) => {
      state.queries[fieldId] = {
        ...state.queries[fieldId],
        ...payload,
      };
    },
    setColumns: (
      state,
      {
        payload: { id, columns },
      }: TAdminPayload<{ columns: TResponsiveTableColumn[] }>,
    ) => {
      if (!state.queries[id]) state.queries[id] = getInitialState({});
      state.queries[id].columns = columns;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkModalValue.pending, (state, payload) => {
      initLoad(state, payload.meta.arg.fieldId);
    });
    builder.addCase(checkModalValue.fulfilled, (state, payload) => {
      finishLoad(state, payload.meta.arg.fieldId);
    });
    builder.addCase(checkModalValue.rejected, (state, payload) => {
      console.error(payload.error);
      finishLoad(state, payload.meta.arg.fieldId);
    });

    builder.addCase(getFiltersConditions.pending, (state, payload) => {
      initLoad(state, payload.meta.arg.fieldId);
    });
    builder.addCase(getFiltersConditions.rejected, (state, payload) => {
      finishLoad(state, payload.meta.arg.fieldId);
    });
    builder.addCase(
      getFiltersConditions.fulfilled,
      (state, { payload, meta }) => {
        state.queries[meta.arg.fieldId].setFilterTypesAction = payload?.action;
        state.queries[meta.arg.fieldId].setFilterTypesConditions =
          payload?.conditions;
        state.queries[meta.arg.fieldId].setFilterTypesTitle = payload?.title;
        finishLoad(state, meta.arg.fieldId);
      },
    );

    builder.addCase(setFiltersConditions.pending, (state, payload) => {
      initLoad(state, payload.meta.arg.fieldId);
    });
    builder.addCase(setFiltersConditions.rejected, (state, payload) => {
      finishLoad(state, payload.meta.arg.fieldId);
    });
    builder.addCase(setFiltersConditions.fulfilled, (state, payload) => {
      finishLoad(state, payload.meta.arg.fieldId);
    });

    addQueryActions(builder);
  },
});

export const useFinderDispatch = (fieldId: string) => {
  const dispatch = useAppDispatch();
  return [
    <T>(action: { payload: T; type: string }) => {
      dispatch({ ...action, payload: { ...action.payload, fieldId } });
    },
    dispatch,
  ];
};

export const useFinderSelector = <TSelected>(
  fieldId: string,
  selector?: (props: TFinderState) => TSelected,
) => {
  const finderState = useAppSelector(
    (globalState) => globalState.querySlice.queries[fieldId],
  );
  if (!selector) return finderState;
  return selector(finderState);
};

export const queryActions = {
  ...querySlice.actions,
  checkModalValue,
  getFiltersConditions,
  getRowData,
  setFiltersConditions,
  sort: sorter,
  search: searcher,
  updateFilters: updateFilter,
  refresh,
  deleteFilters,
  page,
  updateData,
  loadStructure: loadStructures,
  loadSavedFilters: loadSavedFilters,
  setFilters: setFilters,
};

export default querySlice.reducer;
